import { dynamicQuestionContent, getCurrentResponseScore } from "@plura/shared/lead-forms";
import { FormJourneyItem, LeadForm } from "@plura/shared/types";
import { cn } from "@plura/shared/utils";
import { AnimatePresence, motion } from "framer-motion";
import { useFormContext } from "react-hook-form";
import Icon, { type IconName } from "../../../../atoms/Icon";
import RadioGroup from "../../../../molecules/RadioGroup";
import "./rating-screen.css";

function ratingIconNames(icon: string | null | undefined): { on: IconName; off: IconName } {
  const key = (icon ?? "star").toLowerCase();
  if (key === "heart") return { on: "heart-fill", off: "heart" };
  return { on: "star-fill", off: "star-stroke" };
}

interface Props {
  id: string;
  step: FormJourneyItem
  leadForm: LeadForm;
  editorMode: boolean;
  previewMode: boolean;
  active: boolean;
  handleAnswer?: () => void;
  onContentClick?: (fieldName: string, blockId: string) => void;
  hiddenFields?: {
    field: string;
    value: string;
  }[];
}

const RatingScreen = ({ id, step, editorMode, previewMode, leadForm, active, handleAnswer, onContentClick, hiddenFields }: Props) => {
  const minRange = step.minRange ?? 1
  const rangeArray = Array.from({ length: minRange }, (_, i) => i + 1)
  const form = useFormContext();
  const { conversationId, ...userResponses } = form.getValues()
  const currentResponseScore = userResponses
    ? getCurrentResponseScore(userResponses, leadForm)
    : 0

  const handleContentClick = (field: 'content' | 'secondaryContent') => {
    if ((!editorMode || (editorMode && previewMode)) || !onContentClick) return
    onContentClick(field, id)
  }

  return (
    <>
      <div className={cn('questionInner')}>
        <div>
          <h4 className={cn('question')} onClick={() => handleContentClick('content')}>
            <motion.span
              dangerouslySetInnerHTML={{
                __html: dynamicQuestionContent({
                  content: step.content,
                  score: currentResponseScore,
                  editMode: editorMode,
                  previewMode: previewMode ?? false,
                  hiddenFields,
                  journeyItems: leadForm.journeyItems ?? [],
                  userResponses
                })
              }}
            />
          </h4>
          {
            step.secondaryContent &&
            <p className={cn('caption')} onClick={() => handleContentClick('secondaryContent')}>
              <motion.span
                dangerouslySetInnerHTML={{
                  __html: dynamicQuestionContent({
                    content: step.secondaryContent,
                    score: currentResponseScore,
                    editMode: editorMode,
                    previewMode: previewMode ?? false,
                    hiddenFields,
                    journeyItems: leadForm.journeyItems ?? [],
                    userResponses
                  })
                }}
              />
            </p>
          }
        </div>
        <div>
          <motion.div className={'answerInputWrapper'}>
            <AnimatePresence initial={false}>
              <motion.div className={'fieldWrapper'}>
                <div className="rating-screen-range">
                  <RadioGroup
                    id={step.blockId}
                    overrideCheckBoxUI
                    disabledKeySelection={(editorMode && !previewMode) || !active}
                    options={rangeArray.map(value => ({
                      value: value.toString(),
                      label: value.toString()
                    }))}
                    item={(value, checked, selectedValue) => {
                      const valueInt = selectedValue ? parseInt(selectedValue as string) : 0;
                      const lit = checked || valueInt >= parseInt(value, 10);
                      const { on, off } = ratingIconNames(step.icon);

                      return (
                        <div className="rangeItem">
                          <Icon
                            name={lit ? on : off}
                            className={cn(
                              "rating-screen-icon shrink-0 [&_path]:fill-[var(--answer-color,var(--button-color))]",
                              !lit && "opacity-[0.45]"
                            )}
                          />
                          <span className="rating-screen-value">{value}</span>
                        </div>
                      );
                    }}
                    handleOnChange={handleAnswer}
                  />
                </div>
              </motion.div>
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </>
  );
};


export default RatingScreen